import React, { useEffect, useState } from "react";
import {
  Button,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  Box,
  Text,
  VStack,
} from "components-lib";
import axios from "axios";
import { server_url } from "../constants/constants";
import { getAuthHeaders } from "config/api";

// Import modular components
import PatientFormFields from "./components/forms/PatientFormFields";

// Import custom hook
import { usePatientForm } from "./components/hooks/usePatientForm";

/**
 * EditPatientForm Component - Refactored for editing patients
 *
 * Controlled props:
 * - isOpen: boolean to open/close the edit modal (controlled by parent)
 * - onClose: function called when closing the edit modal
 *
 * Optional error modal control (if parent wants to control error modal):
 * - isErrorOpen: boolean to open/close the error modal (optional)
 * - onErrorOpen: function to request opening the error modal (optional)
 * - onErrorClose: function called when closing the error modal (optional)
 */
const EditPatientForm = ({
  onPatientUpdated,
  clinicId,
  doctorId,
  patientId,
  // Controlled modal props
  isOpen = false,
  onClose,
  // Optional error modal control
  isErrorOpen,
  onErrorOpen,
  onErrorClose,
}) => {
  const toast = useToast();

  // Local fallback for error modal if parent doesn't control it
  const [localErrorOpen, setLocalErrorOpen] = useState(false);
  const externalErrorControlled = typeof isErrorOpen !== "undefined";
  const errorModalOpen = externalErrorControlled ? isErrorOpen : localErrorOpen;

  const openErrorModal = () => {
    if (onErrorOpen) onErrorOpen();
    else setLocalErrorOpen(true);
  };
  const closeErrorModal = () => {
    if (onErrorClose) onErrorClose();
    else setLocalErrorOpen(false);
  };

  // Use custom hook for form management
  const {
    newPatientData,
    errorMessage,
    fieldErrors,
    handleInputChange,
    resetForm,
    setNewPatientData,
  } = usePatientForm(clinicId, doctorId, onPatientUpdated);

  // Local state to hold the meta fields from patient_code -> existing_id
  const [patientMeta, setPatientMeta] = useState(null);

  // Ordered keys to render from patient_code through existing_id (inclusive)
  const metaKeysOrder = [
    "patient_code",
    "profile_photo",
    "name",
    "gender",
    "age",
    "dob",
    "email",
    "phone_no",
    "address",
    "city",
    "pincode",
    "language",
    "bloodgroup",
    "existingId",
  ];

  // Fetch patient details on mount / patientId change
  useEffect(() => {
    if (patientId) {
      const fetchPatientDetails = async () => {
        try {
          const response = await axios.get(
            `${server_url}/patients/getDetails/${patientId}`,
          );
          const patient = response.data.data || {};

          // Build a meta object containing ordered fields from patient_code -> existing_id (if present)
          const meta = {};
          metaKeysOrder.forEach((k) => {
            if (Object.prototype.hasOwnProperty.call(patient, k) && patient[k] !== null && patient[k] !== undefined) {
              meta[k] = patient[k];
            }
          });
          setPatientMeta(Object.keys(meta).length ? meta : null);

          // Map patient fields into the form state while preserving existing shape used elsewhere
          setNewPatientData({
            name: patient.name,
            gender: patient.gender,
            age: patient.age,
            dob: patient.dob,
            email: patient.email,
            phoneNumber: patient.phone_no,
            address: patient.address,
            city: patient.city,
            pincode: patient.pincode,
            language: patient.language,
            bloodgroup: patient.bloodgroup,
            // also include additional fields (safe to include; hook should ignore unknowns or accept them)
            patient_code: patient.patient_code,
            profile_photo: patient.profile_photo,
            patient_id: patient.patient_id,
            existingId: patient.existing_id,
          });

          // Optional: show a small success toast (non-breaking)
          toast({
            title: response.data.message || "Patient details retrieved",
            status: "info",
            duration: 2500,
            isClosable: true,
          });

          // Also log meta for debugging
          // eslint-disable-next-line no-console
          console.info("Patient meta fields:", meta);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          openErrorModal();
        }
      };
      fetchPatientDetails();
    }
  }, [patientId, setNewPatientData, toast]); // intentionally not including openErrorModal to avoid re-creation


  const handleUpdatePatient = async () => {
    // basic normalization checks
    const name = newPatientData?.name;
    const gender = newPatientData?.gender;
    const phone = newPatientData?.phoneNumber;
    const age = newPatientData?.age;
    const dob = newPatientData?.dob;

    const isEmpty = (v) =>
      v === null || v === undefined || (typeof v === "string" && v.trim() === "");

    const missing = [];
    if (isEmpty(name)) missing.push("Name");
    if (isEmpty(gender)) missing.push("Gender");
    if (isEmpty(phone)) missing.push("Phone");
    if (isEmpty(age) && isEmpty(dob)) missing.push("Age or DOB");

    if (missing.length > 0) {
      const message = `Please fill required fields: ${missing.join(", ")}`;
      toast({
        title: "Required fields are empty",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return; // abort upload
    }

    try {
      const data = JSON.stringify({
        name: newPatientData.name,
        age: newPatientData.age,
        gender: newPatientData.gender,
        dob: newPatientData.dob,
        email: newPatientData.email,
        number: newPatientData.phoneNumber,
        address: newPatientData.address,
        city: newPatientData.city,
        pincode: newPatientData.pincode,
        language: newPatientData.language,
        bloodgroup: newPatientData.bloodgroup,
        clinic_id: clinicId,
        existing_id: newPatientData.existingId,
      });
      const config = {
        method: "put",
        maxBodyLength: Infinity,
        url: `${server_url}/patients/update/${patientId}`,
        headers: getAuthHeaders(),
        data: data,
      };
      const response = await axios.request(config);
      if (response.data.message === "Patient updated successfully") {
        toast({
          title: "Patient updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        if (onClose) onClose();
        resetForm();
        if (onPatientUpdated) onPatientUpdated();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      	toast({
    title: "Error updating patient",
    description: error?.response?.data?.message || "An error occurred while updating the patient.",
    status: "error",
    duration: 5000,
    isClosable: true,
  });
      // openErrorModal();
    }
  };

  // Handle modal close with form reset
  const handleModalClose = () => {
    if (onClose) onClose();
    resetForm();
  };

  return (
    <>
      {/* Edit Patient Modal - controlled by parent via isOpen/onClose */}
      <Modal isOpen={isOpen} onClose={handleModalClose}>
        <ModalOverlay />
        <ModalContent maxW="600px">
          <ModalHeader>Edit Patient</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {errorMessage && (
              <Alert status="error" mb={4}>
                <AlertIcon />
                {errorMessage}
              </Alert>
            )}

            {/* If patientMeta exists, render an info alert listing field: value */}
            {/* {patientMeta && (
              <Alert status="info" mb={4} flexDirection="column" alignItems="flex-start">
                <AlertIcon />
                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Retrieved patient fields:
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {Object.entries(patientMeta).map(([key, val]) => (
                      <Text key={key}>
                        <Text as="span" fontWeight="bold" mr={2}>
                          {key}:
                        </Text>
                        <Text as="span">{String(val)}</Text>
                      </Text>
                    ))}
                  </VStack>
                </Box>
              </Alert>
            )} */}

            {/* Patient Form Fields Component */}
            <PatientFormFields
              patientData={newPatientData}
              onInputChange={handleInputChange}
              fieldErrors={fieldErrors}
            />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleUpdatePatient}>
              Update
            </Button>
            <Button variant="ghost" onClick={handleModalClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Error Modal - can be controlled by parent or locally */}
      <Modal isOpen={errorModalOpen} onClose={closeErrorModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Error</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="error">
              <AlertIcon />
              {errorMessage || "An error occurred"}
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={closeErrorModal}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditPatientForm;





